import React from 'react';
import { useOrganizationClientRecords } from '../../../../../../../hooks/api/useOrganizationClientRecords';
import { OrganizationTypes } from '../../../../../../../primary-record-types';
import Stack, { ContentStack } from '../../../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import { clientRecordStatusTypeGuard } from '../../../../../../../utils/clientRecordsUtil';
import { getIntl } from '../../../../../../../utils/intl';
import InfoIcon from '../../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../../v2BaseComponents/Prompt';
import getName from '../../../../../../../utils/getName';

const intl = getIntl('re-engage-client-prompt-content', {
    reEngageClient: 'Re-Engage Client',
    reEngageClientPrompt:
        'Are you sure you want to re-engage {clientName}?  This will set the client into a pending invitation state.',
    reEngage: 'Re-Engage',
    cancel: 'Cancel',
});

export type ReEngageClientPromptContentOwnProps = {
    onClose: () => void;
    organizationClientRecordId: string | undefined;
    onReEngage: (organizationClientRecordId: string) => void;
};

export type ReEngageClientPromptContentProps = ReEngageClientPromptContentOwnProps;

const Content: React.FC<{
    clientRecord: OrganizationTypes.OrganizationClientRecordForStatus<'inactive'>;
    onReEngageClientRecord: () => void;
    onClose: () => void;
}> = ({ clientRecord, onReEngageClientRecord, onClose }) => (
    <ContentStack>
        <InfoIcon
            alertIconProps={{
                name: 'RefreshCcw',
                variant: 'info',
            }}
            title={intl('reEngageClient')}
            subText={intl('reEngageClientPrompt', {
                clientName: getName(clientRecord.clientDetails) || '',
                pointOfContactName: getName(clientRecord.clientOrganizerDetails) || '',
            })}
        />
        <PromptActions
            actions={[
                {
                    title: intl('reEngage'),
                    onPress: onReEngageClientRecord,
                    icon: 'Send',
                    variant: 'primary',
                    testID: 'confirm-re-engage-client',
                },
                {
                    title: intl('cancel'),
                    onPress: onClose,
                    variant: 'secondary',
                    icon: 'XCircle',
                    testID: 'cancel-re-engage-client',
                },
            ]}
        />
    </ContentStack>
);

const ReEngageClientPromptContent: React.FC<ReEngageClientPromptContentProps> = ({
    onClose,
    organizationClientRecordId,
    onReEngage,
}) => {
    const { isLoadingClientRecord, clientRecord } = useOrganizationClientRecords({
        onReEngageClientRecordSuccess: onClose,
        organizationClientRecordId,
    });

    const isLoading = isLoadingClientRecord || !clientRecord || !clientRecordStatusTypeGuard.inactive(clientRecord);

    return (
        <Stack vertical fullWidth style={{ minHeight: 200 }}>
            <AnimateLoadedContent isLoading={isLoading}>
                {() =>
                    clientRecord && clientRecordStatusTypeGuard.inactive(clientRecord) ? (
                        <Content
                            clientRecord={clientRecord}
                            onReEngageClientRecord={() => onReEngage(clientRecord.organizationClientRecordId)}
                            onClose={onClose}
                        />
                    ) : null
                }
            </AnimateLoadedContent>
        </Stack>
    );
};

export default React.memo(ReEngageClientPromptContent);
